import { Component, inject, input, output, model, computed, signal, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterProductSearch } from '../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterSelectComponent } from '../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterSortingComponent } from '../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import {
  StockFilters,
  StockType,
  ProductTitleResult,
  StockSortOption,
} from '../../../../pages/admin-dashboard/stock-management/stock.model';
import { AdminSuppliersList } from '../../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { ProductService } from '../../../../services/product/product.service';

@Component({
  selector: 'app-stock-filters',
  standalone: true,
  imports: [
    FormsModule,
    FilterProductSearch,
    FilterSelectComponent,
    FilterSortingComponent,
  ],
  templateUrl: './stock-filters-component.html',
  styleUrl: './stock-filters-component.scss',
})
export class StockFiltersComponent {
  private locale         = inject(LOCALE_ID);
  private productService = inject(ProductService);

  stockTypes          = input.required<StockType[]>();
  isCurrentStockMode  = input.required<boolean>();
  suppliers           = input.required<AdminSuppliersList[]>();
  productTitleLoading = input.required<boolean>();
  productTitleResults = input.required<ProductTitleResult[]>();
  isProductLevel      = input<boolean>(false);
  hasSubProducts      = input<boolean>(false);
  parentProductId     = input<number | undefined>(undefined);

  filters                  = model.required<StockFilters>();
  productSearchTerm        = model.required<string>();
  showProductTitleDropdown = model.required<boolean>();

  applyFilters       = output<void>();
  resetFilters       = output<void>();
  toggleSortOrder    = output<void>();
  selectProductTitle = output<ProductTitleResult>();
  productTitleFocus  = output<void>();
  searchChange       = output<string>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  subProductSearchTerm        = signal<string>('');
  showSubProductDropdown      = signal<boolean>(false);
  subProductLoading           = signal<boolean>(false);
  subProductResults           = signal<ProductTitleResult[]>([]);
  private subProductTimeout: ReturnType<typeof setTimeout> | null = null;

  dateRangeError = computed(() => {
    const from = this.filters().expDateFrom;
    const to   = this.filters().expDateTo;
    return from && to ? new Date(to) < new Date(from) : false;
  });

  stockSortOptions = computed<StockSortOption[]>(() => {
    if (this.isCurrentStockMode()) {
      return [
        { value: 'LAST_MODIFIED_ON', labelEn: 'Last Modified On', labelAr: 'آخر تعديل' },
        { value: 'EXP_DATE',         labelEn: 'Expiry Date',      labelAr: 'تاريخ الانتهاء' },
        { value: 'QUANTITY',         labelEn: 'Quantity',         labelAr: 'الكمية' },
      ];
    }
    return [
      { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    ];
  });

  onSubProductSearch(term: string): void {
    this.subProductSearchTerm.set(term);
    this.filters.update((f) => ({ ...f, productId: undefined }));
    if (this.subProductTimeout) clearTimeout(this.subProductTimeout);

    if (term.length < 2) {
      this.showSubProductDropdown.set(false);
      this.subProductResults.set([]);
      return;
    }

    this.showSubProductDropdown.set(true);
    this.subProductLoading.set(true);

    this.subProductTimeout = setTimeout(() => {
      this.searchSubProducts(term);
    }, 300);
  }

  onSubProductFocus(): void {
    if (this.subProductSearchTerm().length >= 2) {
      this.showSubProductDropdown.set(true);
      if (!this.subProductResults().length) {
        this.searchSubProducts(this.subProductSearchTerm());
      }
    }
  }

  private searchSubProducts(term: string): void {
  const parentId = this.parentProductId();
  if (!parentId) return;

  this.productService.autocompleteAdminProducts(term, parentId).subscribe({
    next: (res) => {
      // this.subProductResults.set(
      //   res.list?.map((p) => ({
      //     id:      p.id,
      //     titleAr: p.titleAr ?? '',
      //     titleEn: p.titleEn ?? '',
      //   })) ?? [],
      // );
      this.subProductResults.set(
  res.list?.map((p) => ({
    id: p.id,
    titleAr: p.titleAr ?? '',
    titleEn: p.titleEn ?? '',
    image: '/assets/product.png',
    manufacturerCompanyName: 'Manufacturer Company',
  })) ?? [],
);
      this.subProductLoading.set(false);
    },
    error: () => {
      this.subProductResults.set([]);
      this.subProductLoading.set(false);
    },
  });
}

  onSelectSubProduct(item: ProductTitleResult): void {
    this.filters.update((f) => ({ ...f, productId: item.id }));
    this.subProductSearchTerm.set(
      this.isArabic() ? item.titleAr || item.titleEn : item.titleEn || item.titleAr,
    );
    this.showSubProductDropdown.set(false);
  }

  updateFilterField(field: keyof StockFilters, value: StockFilters[keyof StockFilters]): void {
    this.filters.update((prev) => ({ ...prev, [field]: value }));
  }

  onExpDateFromChange(value: string): void {
    this.filters.update((prev) => ({ ...prev, expDateFrom: value || null }));
  }

  onExpDateToChange(value: string): void {
    this.filters.update((prev) => ({ ...prev, expDateTo: value || null }));
  }

  onApply(): void {
    if (!this.dateRangeError()) this.applyFilters.emit();
  }
}